import { useEffect, useState } from "react";
import { ethers } from "ethers";
import { useAppKitProvider, useAppKitAccount } from "@reown/appkit/react";
import type { Eip1193Provider } from "ethers";

export const useSmartWalletSigner = () => {
  // Obtain wallet provider and connection info from AppKit
  const { walletProvider } = useAppKitProvider("eip155");
  const { isConnected, address } = useAppKitAccount({ namespace: "eip155" });

  // Local state for signer, loading status, and error message
  const [signer, setSigner] = useState<ethers.JsonRpcSigner | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const setupSigner = async () => {
      setLoading(true);
      setError(null);

      try {
        // Only proceed if wallet is connected with an address
        if (walletProvider && isConnected && address) {
          // Cast AppKit provider to EIP-1193 standard
          const eipProvider = walletProvider as Eip1193Provider;
          // Wrap in ethers BrowserProvider
          const provider = new ethers.BrowserProvider(eipProvider);
          // Create signer bound to the connected address
          const smartSigner = new ethers.JsonRpcSigner(provider, address);

          if (isMounted) setSigner(smartSigner);
        } else {
          // Reset signer when disconnected
          if (isMounted) setSigner(null);
        }
      } catch (err) {
        if (isMounted) {
          console.error("❌ Failed to setup signer:", err);
          setError("Failed to setup signer");
          setSigner(null);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    setupSigner();

    // Cleanup on unmount
    return () => {
      isMounted = false;
      setSigner(null);
      setLoading(false);
      setError(null);
    };
  }, [walletProvider, isConnected, address]);

  return { signer, isConnected, address, loading, error };
};
